import { Lightbulb } from "lucide-react";
import { skincareKnowledgeData } from "./data/skincare-knowledge";
import { skinScienceIngredientsData } from "./data/skin-science-ingredients";
import { customerServiceData } from "./data/customer-service";
import { beautyTrendsTechniquesData } from "./data/beauty-trends-techniques";
import { safetyData } from "./data/safety";
import { makeupKnowledgeData } from "./data/makeup-knowledge";
import { fragranceKnowledgeData } from "./data/fragrance-knowledge";

// Utility function to format time ago
export const formatTimeAgo = (date: Date) => {
  const now = new Date();
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60),
  );
  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  return `${Math.floor(diffInMinutes / 1440)}d ago`;
};

export const formatDuration = (minutes: number) => {
  if (!minutes) return "-";
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
    case "passed":
      return "bg-green-900/30 text-green-400 border-green-600/30";
    case "inactive":
      return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    case "training":
    case "running":
      return "bg-blue-900/30 text-blue-400 border-blue-600/30";
    case "error":
    case "failed":
      return "bg-red-900/30 text-red-400 border-red-600/30";
    case "pending":
      return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
    default:
      return "bg-gray-900/30 text-gray-400 border-gray-600/30";
  }
};

// Helper function to calculate total tests recursively
export const calculateTotalTests = (
  node: any,
): { passed: number; failed: number; total: number } => {
  // Handle null/undefined nodes
  if (!node) {
    return { passed: 0, failed: 0, total: 0 };
  }

  let passed = 0;
  let failed = 0;

  // Count tests in the tests array (for original data structure)
  if (node.tests && Array.isArray(node.tests)) {
    passed = node.tests.filter((test: any) => test.success).length;
    failed = node.tests.length - passed;
  }

  // Count individual test nodes (for transformed data structure)
  if (node.isTest && node.testData) {
    return {
      passed: node.testData.success ? 1 : 0,
      failed: node.testData.success ? 0 : 1,
      total: 1,
    };
  }

  // Recursively count tests in children
  if (node.children) {
    for (const child of node.children) {
      const childTotals = calculateTotalTests(child);
      passed += childTotals.passed;
      failed += childTotals.failed;
    }
  }

  return { passed, failed, total: passed + failed };
};

// Function to extract recommendations from data files
export const extractRecommendationsFromData = () => {
  const recommendations: any[] = [];

  // Import all data files
  const dataFiles = [
    { name: "Skincare Knowledge", data: skincareKnowledgeData },
    { name: "Skin Science & Ingredients", data: skinScienceIngredientsData },
    { name: "Customer Service", data: customerServiceData },
    { name: "Beauty Trends & Techniques", data: beautyTrendsTechniquesData },
    { name: "Safety & Security", data: safetyData },
    { name: "Makeup Knowledge", data: makeupKnowledgeData },
    { name: "Fragrance Knowledge", data: fragranceKnowledgeData },
  ];

  // Extract recommendations from each data file
  dataFiles.forEach((file) => {
    const extractFromNode = (node: any, parentName?: string) => {
      if (node.recommendation && node.recommendation !== "PLACEHOLDER") {
        const totals = calculateTotalTests(node);
        const passRate =
          totals.total > 0 ? (totals.passed / totals.total) * 100 : 0;

        // Determine priority based on pass rate
        let priority: "critical" | "high" | "medium" | "low" = "medium";
        if (passRate < 50) priority = "critical";
        else if (passRate < 70) priority = "high";
        else if (passRate < 85) priority = "medium";
        else priority = "low";

        recommendations.push({
          id: node.id,
          title: node.name,
          description: `${passRate.toFixed(1)}% pass rate - ${node.name}`,
          priority,
          topic: parentName || file.name,
          actions: [node.recommendation],
          icon: Lightbulb,
          failedTests:
            totals.failed > 0
              ? [
                  {
                    id: `${node.id}-failed`,
                    testName: `${node.name} Tests`,
                    input: "Multiple test scenarios",
                    expectedOutput: "All tests should pass",
                    actualOutput: `${totals.failed} out of ${totals.total} tests failed`,
                    responseTime: 0,
                    severity: priority as "high" | "medium" | "low",
                  },
                ]
              : [],
          nodeData: node,
          passRate,
          totalTests: totals.total,
          failedTestsCount: totals.failed,
        });
      }

      // Recursively check children
      if (node.children) {
        node.children.forEach((child: any) => {
          extractFromNode(child, node.name || parentName);
        });
      }
    };

    extractFromNode(file.data);
  });

  return recommendations;
};
